import axios from "axios";

export const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "http://localhost:8000").replace(/\/$/, "");
export const API = `${BACKEND_URL}/api`;

export const normalizeApiDetail = (detail, fallback = "Terjadi kesalahan") => {
  if (typeof detail === "string" && detail.trim()) return detail.trim();
  if (Array.isArray(detail)) {
    const messages = detail
      .map((row) => {
        if (typeof row === "string") return row;
        if (!row || typeof row !== "object") return "";
        const loc = Array.isArray(row.loc) ? row.loc.filter((x) => x !== "body").join(".") : "";
        const msg = String(row.msg || row.message || "").trim();
        return [loc, msg].filter(Boolean).join(": ");
      })
      .filter(Boolean);
    if (messages.length) return messages.join("; ");
  }
  if (detail && typeof detail === "object") {
    const msg = String(detail.message || detail.msg || detail.error || "").trim();
    if (msg) return msg;
  }
  return fallback;
};

export const apiErrorMessage = (error, fallback = "Terjadi kesalahan") => {
  if (!error) return fallback;
  if (!error.response) {
    if (error.code === "ECONNABORTED") return "Permintaan timeout. Silakan coba lagi.";
    return error.message || "Tidak dapat terhubung ke server.";
  }
  const status = Number(error.response.status || 0);
  const responseData = error.response?.data;

  // nginx/proxy errors commonly arrive as text/html instead of JSON.
  // Never treat a string response as a mutable API detail object.
  if (typeof responseData === "string") {
    if (status === 502) return "Backend tidak dapat dijangkau (502 Bad Gateway). Periksa service kraya-backend.";
    if (status === 503) return "Backend sedang tidak tersedia (503 Service Unavailable).";
    if (status === 504) return "Backend timeout (504 Gateway Timeout).";
  }

  const detail = normalizeApiDetail(
    responseData && typeof responseData === "object" ? responseData.detail : null,
    ""
  );
  if (detail) return detail;
  const message = normalizeApiDetail(
    responseData && typeof responseData === "object" ? responseData.message : null,
    ""
  );
  if (message) return message;
  if (status === 401) return "Sesi login berakhir. Silakan login kembali.";
  if (status === 403) return "Anda tidak memiliki akses untuk tindakan ini.";
  if (status === 409) return "Data berubah atau konflik dengan kondisi saat ini. Silakan refresh.";
  if (status === 422) return "Data yang dikirim belum valid.";
  if (status >= 500) return "Server mengalami kendala. Silakan coba lagi.";
  return fallback;
};

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  timeout: 45000,
});

// Rolling migration only: legacy sessions created before HttpOnly-cookie auth
// can still send their token. New first-party sessions never persist a JWT.
api.interceptors.request.use((cfg) => {
  const legacyToken = localStorage.getItem("pos_token");
  if (legacyToken) cfg.headers.Authorization = `Bearer ${legacyToken}`;
  return cfg;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const responseData = error?.response?.data;
    if (
      responseData &&
      typeof responseData === "object" &&
      !Array.isArray(responseData) &&
      typeof responseData.detail !== "string"
    ) {
      responseData.detail = normalizeApiDetail(
        responseData.detail,
        error.response.status === 422 ? "Data yang dikirim belum valid." : "Terjadi kesalahan"
      );
    }
    if (error?.response?.status === 401) {
      localStorage.removeItem("pos_token");
      // Do not force-navigation here: public pages also probe /auth/me.
      // AuthProvider owns session state and Protected routes own redirection.
      window.dispatchEvent(new Event("kraya-auth-expired"));
    }
    return Promise.reject(error);
  }
);

export const apiAssetUrl = (path) => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  return `${BACKEND_URL}${path.startsWith("/") ? path : `/${path}`}`;
};

export const openProtectedFile = async (path) => {
  if (!path) throw new Error("Path file tidak tersedia");
  const normalizedPath = path.replace(/^\/api(?=\/|$)/, "");
  const response = await api.get(normalizedPath, { responseType: "blob" });
  const blobUrl = URL.createObjectURL(response.data);
  const opened = window.open(blobUrl, "_blank", "noopener,noreferrer");
  if (!opened) {
    const anchor = document.createElement("a");
    anchor.href = blobUrl;
    anchor.target = "_blank";
    anchor.rel = "noopener noreferrer";
    anchor.click();
  }
  window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
};

export const formatIDR = (n) => {
  if (n === null || n === undefined || isNaN(n)) return "Rp 0";
  return "Rp " + Math.round(Number(n)).toLocaleString("id-ID");
};

export const formatDate = (iso) => {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("id-ID", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};
